/**
 * The Home opening (contexts/05, "The Home opening"): a four-second clip with its sound — the raptor
 * close-up fading to white — over the whole window, when a signed-in person reaches Home, at most
 * once every ten days per person and browser. Drawn inside `.shell` beside the entrance's provider, fixed over the header bar,
 * the rail and the page alike. Home renders and loads underneath and is usable whatever the media
 * does: a refused `play()` (a cold URL load has no user activation for Chrome to allow sound, and
 * its media engagement score lets it through only sometimes) holds the poster with a Play button,
 * whose click plays the clip with its sound (Dakota, 2026-10-26: never muted); an error, no first frame within the startup
 * timeout, Skip, Escape, a hidden tab, reduced motion switched on, leaving Home or signing out all
 * close it at once. The clean ending: the clip's own fade to white, joined by a white layer over the
 * whole window, a short hold, then the white fades away to reveal Home.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import openingVideo from '../assets/home-opening/unitedos-home-opening.mp4';
import openingPoster from '../assets/home-opening/unitedos-home-opening-poster.webp';
import {
  OPENING_OVERALL_TIMEOUT_MS,
  OPENING_REVEAL_MS,
  OPENING_STARTUP_TIMEOUT_MS,
  OPENING_WHITE_HOLD_MS,
  inWhiteWindow,
  isHomeRoute,
  markOpeningTried,
  shouldPlayOpening,
} from '../lib/homeOpening.js';
import { canPlayEntrance, type BrowserTraits } from '../lib/meetingEntrance.js';
import { useSessionContext } from '../lib/session.js';
import { browserStorage } from '../lib/storedFlag.js';

/** `waiting`: Chrome refused the sound without a click, so the poster holds with a Play button. */
type Phase = 'idle' | 'playing' | 'waiting' | 'white' | 'revealing';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function browserTraits(): BrowserTraits {
  const nav = navigator as Navigator & { userAgentData?: { brands: readonly { brand: string }[]; mobile: boolean } };
  return {
    userAgent: nav.userAgent,
    brands: nav.userAgentData?.brands,
    mobile: nav.userAgentData?.mobile,
    reducedMotion: window.matchMedia?.(REDUCED_MOTION).matches ?? false,
  };
}

export function HomeOpening() {
  const { session } = useSessionContext();
  const userId = session?.user.id ?? '';
  const { pathname } = useLocation();
  // Chrome on a desktop renders the player at all; Safari and the rest never see a <video> or its URL.
  const chrome = useMemo(() => canPlayEntrance({ ...browserTraits(), reducedMotion: false }), []);

  const videoRef = useRef<HTMLVideoElement>(null);
  const skipRef = useRef<HTMLButtonElement>(null);
  const tried = useRef(new Set<string>());
  const timers = useRef<number[]>([]);
  const phaseRef = useRef<Phase>('idle');
  const firstFrame = useRef(false);
  const armed = useRef(false); // the overlay is up, the media not yet started
  const [phase, setPhaseState] = useState<Phase>('idle');

  const setPhase = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  /** Closes the opening at once: silence first, then the overlay; Home is already underneath. */
  const stop = useCallback(() => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    const v = videoRef.current;
    if (v) {
      v.pause();
      v.removeAttribute('src');
      v.load(); // drops the buffered clip and its decoder
    }
    firstFrame.current = false;
    if (phaseRef.current !== 'idle') setPhase('idle');
  }, [setPhase]);

  /** The clip's fade to white has begun: the white layer joins it, so the whole window — margins included — is white at the end. */
  const whiten = useCallback(() => {
    if (phaseRef.current === 'playing') setPhase('white');
  }, [setPhase]);

  /** The clean ending: white holds briefly, then fades away to reveal Home (CSS `.opening--revealing`). */
  const onEnded = () => {
    if (phaseRef.current === 'idle' || phaseRef.current === 'revealing') return;
    whiten();
    later(() => {
      setPhase('revealing');
      later(stop, OPENING_REVEAL_MS);
    }, OPENING_WHITE_HOLD_MS);
  };

  /** The clip is under way: no first frame soon, or anything left on screen too long, closes it. */
  const started = useCallback(() => {
    later(() => {
      if (!firstFrame.current) stop();
    }, OPENING_STARTUP_TIMEOUT_MS);
    later(stop, OPENING_OVERALL_TIMEOUT_MS);
  }, [later, stop]);

  // The arrival at Home is the trigger, in two steps. Reduced motion is read at that moment, not at
  // mount: the setting can change while the app is open.
  const eligible = () => shouldPlayOpening({ pathname, userId, canPlay: canPlayEntrance(browserTraits()), store: browserStorage(), tried: tried.current, now: Date.now() });
  // 1. Before paint: the overlay and its poster are in the first frame Home draws, never a flash of
  //    the page. Nothing is marked or fetched yet.
  useLayoutEffect(() => {
    const v = videoRef.current;
    if (!chrome || !v || phaseRef.current !== 'idle' || !eligible()) return;
    v.poster = openingPoster;
    armed.current = true;
    setPhase('playing');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `eligible` reads the same three values
  }, [chrome, pathname, userId, setPhase]);
  // 2. After every effect has settled, in a microtask the cleanup cancels: the mark, the download
  //    and `play()`. In development, StrictMode runs every effect, its cleanup and the effect again
  //    before any microtask — the stop in those cleanups would strip a start made in step 1, and its
  //    mark would then keep the opening from ever playing — so the start waits until they are done
  //    and re-arms itself if the dance closed the overlay.
  useEffect(() => {
    if (!chrome) return;
    let cancelled = false;
    queueMicrotask(() => {
      const v = videoRef.current;
      if (cancelled || !v || !armed.current) return;
      armed.current = false;
      if (phaseRef.current !== 'playing') {
        if (!eligible()) return;
        setPhase('playing');
      }
      // Tried from this moment: a refresh, a Skip or a failure never replays it for ten days.
      markOpeningTried(userId, browserStorage(), tried.current, Date.now());
      v.preload = 'auto';
      v.src = openingVideo;
      v.muted = false;
      v.currentTime = 0;
      // With sound where the page has a user activation (the sign-in's own click, a link to Home).
      // Without one — a cold URL load, a redirect long after sign-in — Chrome refuses unless the site's
      // media engagement score happens to be high, which is why the sound came and went (Dakota,
      // 2026-10-26). Then the poster holds with a Play button: that click is the activation, and the
      // clip plays with its sound every time. Nothing plays muted any more.
      v.play().then(started, () => {
        if (phaseRef.current !== 'playing') return;
        setPhase('waiting');
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `eligible` reads the same three values
  }, [chrome, pathname, userId, started, setPhase, stop]);

  /** Play, clicked on the held poster: the click is Chrome's user activation, so the sound is allowed. */
  const playWithSound = () => {
    const v = videoRef.current;
    if (!v || phaseRef.current !== 'waiting') return;
    setPhase('playing');
    v.muted = false;
    v.play().then(started, stop);
  };

  const onPlaying = () => {
    firstFrame.current = true;
    // Frame-accurate where Chrome offers it; `timeupdate` (every ~250 ms) is the fallback.
    const v = videoRef.current;
    if (!v || typeof v.requestVideoFrameCallback !== 'function') return;
    const watch = () => {
      if (phaseRef.current !== 'playing' || !videoRef.current) return;
      if (inWhiteWindow(videoRef.current.currentTime, videoRef.current.duration)) whiten();
      else videoRef.current.requestVideoFrameCallback(watch);
    };
    v.requestVideoFrameCallback(watch);
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (v && inWhiteWindow(v.currentTime, v.duration)) whiten();
  };

  // Leaving Home closes it.
  useEffect(() => {
    if (phase !== 'idle' && !isHomeRoute(pathname)) stop();
  }, [pathname, phase, stop]);

  // Escape, a hidden tab and reduced motion switched on close it; Skip takes the focus so Enter or Space also does.
  useEffect(() => {
    if (phase === 'idle') return;
    skipRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') stop();
    };
    const motion = window.matchMedia?.(REDUCED_MOTION);
    const onMotion = () => {
      if (motion?.matches) stop();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('visibilitychange', onVisibility);
    motion?.addEventListener('change', onMotion);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('visibilitychange', onVisibility);
      motion?.removeEventListener('change', onMotion);
    };
  }, [phase, stop]);

  // Another person signing in silences it.
  useEffect(() => stop, [userId, stop]);
  // So does the shell going away (sign-out). The element is held from mount: by the time an
  // unmount's cleanup runs, React has already emptied the ref, and a detached video keeps playing.
  useEffect(() => {
    const v = videoRef.current;
    const pending = timers; // the list is replaced on every stop; read it at cleanup
    return () => {
      pending.current.forEach((t) => window.clearTimeout(t));
      if (!v) return;
      v.pause();
      v.removeAttribute('src');
      v.load();
    };
  }, []);

  if (!chrome) return null;
  const active = phase !== 'idle';
  const white = phase === 'white' || phase === 'revealing';
  return (
    <div
      className={`opening${active ? ' opening--active' : ''}${white ? ' opening--white' : ''}${phase === 'revealing' ? ' opening--revealing' : ''}`}
      role={active ? 'dialog' : undefined}
      aria-modal={active ? true : undefined}
      aria-label="Home opening"
    >
      <video ref={videoRef} className="opening__video" preload="none" playsInline disablePictureInPicture aria-hidden="true" onPlaying={onPlaying} onTimeUpdate={onTimeUpdate} onEnded={onEnded} onError={stop} />
      <div className="opening__white" aria-hidden="true" />
      {phase === 'waiting' && (
        <button type="button" className="opening__play" onClick={playWithSound} aria-label="Play the intro with sound">
          ▶
        </button>
      )}
      {(phase === 'playing' || phase === 'waiting') && (
        <button type="button" ref={skipRef} className="opening__skip" onClick={stop}>
          Skip intro
        </button>
      )}
    </div>
  );
}
